/** Path helpers and nav types for the admin sidebar. */

import type React from "react"

/** Module home → additional path prefixes that belong to the same module. */
const MODULE_ALIASES: Record<string, string[]> = {
  "/customer-management": ["/customers"],
  "/employee-management": ["/employees"],
  "/complaint-management": ["/complaints"],
  "/area-zone-management": ["/areas"],
  "/vendor-management": ["/vendors"],
}

export type SidebarIcon = React.ComponentType<{ className?: string }>

export type SidebarLeaf = {
  title: string
  path: string
  icon: SidebarIcon
  exact?: boolean
}

export type SidebarNestedGroup = {
  id: string
  title: string
  icon: SidebarIcon
  isDropdown: true
  pathPrefix?: string
  subItems: SidebarLeaf[]
}

export type SidebarNavChild = SidebarLeaf | SidebarNestedGroup

export type SidebarGroup = {
  id: string
  title: string
  icon: SidebarIcon
  isDropdown: true
  pathPrefix?: string
  subItems: SidebarNavChild[]
}

export type SidebarLink = {
  id: string
  title: string
  icon: SidebarIcon
  isDropdown?: false
  path: string
}

export type SidebarItem = SidebarGroup | SidebarLink

export function isGroup(item: SidebarItem): item is SidebarGroup {
  return item.isDropdown === true
}

export function isNestedGroup(item: SidebarNavChild): item is SidebarNestedGroup {
  return (item as SidebarNestedGroup).isDropdown === true
}

export function flattenLeaves(items: SidebarNavChild[]): SidebarLeaf[] {
  const out: SidebarLeaf[] = []
  for (const item of items) {
    if (isNestedGroup(item)) out.push(...item.subItems)
    else out.push(item)
  }
  return out
}

export function prefixMatches(pathname: string, prefix?: string): boolean {
  if (!prefix) return false
  return pathname === prefix || pathname.startsWith(prefix + "/")
}

export function isPathActive(pathname: string, targetPath: string, exact = false): boolean {
  if (!targetPath) return false
  if (pathname === targetPath) return true
  if (exact) return false
  if (pathname.startsWith(targetPath + "/")) return true
  const aliases = MODULE_ALIASES[targetPath]
  if (aliases) {
    return aliases.some((alias) => pathname === alias || pathname.startsWith(alias + "/"))
  }
  return false
}

export function isAnyPathActive(pathname: string, paths: string[]): boolean {
  return paths.some((p) => isPathActive(pathname, p))
}

export function leafIsActive(pathname: string, leaf: SidebarLeaf): boolean {
  return isPathActive(pathname, leaf.path, !!leaf.exact)
}

export function nestedGroupIsActive(pathname: string, group: SidebarNestedGroup): boolean {
  if (prefixMatches(pathname, group.pathPrefix)) return true
  return group.subItems.some((leaf) => leafIsActive(pathname, leaf))
}

export function groupIsActive(pathname: string, item: SidebarGroup): boolean {
  if (prefixMatches(pathname, item.pathPrefix)) return true
  return item.subItems.some((sub) =>
    isNestedGroup(sub) ? nestedGroupIsActive(pathname, sub) : leafIsActive(pathname, sub),
  )
}

export function itemIsActive(pathname: string, item: SidebarItem): boolean {
  return isGroup(item) ? groupIsActive(pathname, item) : isPathActive(pathname, item.path)
}

export function collectGroupPaths(item: SidebarGroup): string[] {
  return flattenLeaves(item.subItems).map((leaf) => leaf.path)
}

export function collectNestedGroupIds(pathname: string, items: SidebarItem[]): string[] {
  const ids: string[] = []
  for (const item of items) {
    if (!isGroup(item)) continue
    for (const sub of item.subItems) {
      if (isNestedGroup(sub) && nestedGroupIsActive(pathname, sub)) ids.push(sub.id)
    }
  }
  return ids
}
